import MfaSettings from './security/MfaSettings';
import React, { useState, useEffect } from 'react';
import {
  User,
  Mail,
  Shield,
  Calendar,
  Save,
  Eye,
  EyeOff,
  Upload,
  Camera,
  Lock,
  Activity,
  Settings,
  Clock,
  X,
  AlertCircle
} from 'lucide-react';
import authService from '../services/authService';
import { usersService } from '../services/supabaseService';
import { supabase } from '../services/supabaseService';
import { User as UserType } from '../types';
import UserAvailabilityModal from './UserAvailabilityModal';
import { availabilityService } from '../services/availabilityService';
import { validateImageFile, compressImage } from '../utils/imageUtils';

const UserProfile: React.FC = () => {
  const [user, setUser] = useState<UserType | null>(null);
  const [userStats, setUserStats] = useState({
    daysInSystem: 0,
    daysSinceLastLogin: 0,
    totalContacts: 0,
    totalActivities: 0,
    totalMeetings: 0,
    passwordLastChanged: null as Date | null
  });
  const [loadingStats, setLoadingStats] = useState(true);
  const [isEditing, setIsEditing] = useState(false);
  const [isSaving, setSaving] = useState(false);
  const [showPasswordChange, setShowPasswordChange] = useState(false);
  const [showAvailabilityModal, setShowAvailabilityModal] = useState(false);
  const [imageError, setImageError] = useState<string | null>(null);
  const [imageLoading, setImageLoading] = useState(false);
  const [availabilityData, setAvailabilityData] = useState<any>(null);
  const [loadingAvailability, setLoadingAvailability] = useState(false);
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    avatar: ''
  });
  const [passwordData, setPasswordData] = useState({
    currentPassword: '',
    newPassword: '',
    confirmPassword: ''
  });
  const [passwordError, setPasswordError] = useState<string | null>(null);
  const [passwordSuccess, setPasswordSuccess] = useState<string | null>(null);
  const [isChangingPassword, setIsChangingPassword] = useState(false);
  const [profileError, setProfileError] = useState<string | null>(null);
  const [profileSuccess, setProfileSuccess] = useState<string | null>(null);
  const [emailChangePending, setEmailChangePending] = useState(false);

  useEffect(() => {
    loadUserProfile();
    loadUserStats();
    loadUserAvailability();
  }, []);

  const loadUserProfile = () => {
    const session = authService.getCurrentSession();
    if (session?.user) {
      setUser(session.user);
      setFormData({
        name: session.user.name,
        email: session.user.email,
        avatar: session.user.avatar || ''
      });
    }
  };

  const loadUserStats = async () => {
    try {
      setLoadingStats(true);
      const session = authService.getCurrentSession();
      if (!session?.user) return;

      const userId = session.user.id;
      const userName = session.user.name;
      const userEmail = session.user.email;
      const userCreatedAt = session.user.createdAt;
      const userLastLogin = session.user.lastLogin;

      // Calculate days in system
      const daysInSystem = userCreatedAt 
        ? Math.floor((Date.now() - userCreatedAt.getTime()) / (1000 * 60 * 60 * 24))
        : 0;

      // Calculate days since last login
      const daysSinceLastLogin = userLastLogin 
        ? Math.floor((Date.now() - userLastLogin.getTime()) / (1000 * 60 * 60 * 24))
        : 0;

      // contacts.owner and activities.owner are VARCHAR holding the user's name,
      // not their UUID (see 20250824132730_patient_smoke and the RLS policy in
      // 20251004215139 which does `owner = (SELECT name FROM users WHERE id = auth.uid())`).
      // meetings has both `owner` and `assigned_user_id uuid` — we filter by the UUID.
      const [contactsResult, activitiesResult, meetingsResult] = await Promise.allSettled([
        supabase
          .from('contacts')
          .select('id', { count: 'exact', head: true })
          .eq('owner', userName),

        supabase
          .from('activities')
          .select('id', { count: 'exact', head: true })
          .eq('owner', userName),

        supabase
          .from('meetings')
          .select('id', { count: 'exact', head: true })
          .eq('assigned_user_id', userId)
      ]);

      const totalContacts = contactsResult.status === 'fulfilled' ? contactsResult.value.count || 0 : 0;
      const totalActivities = activitiesResult.status === 'fulfilled' ? activitiesResult.value.count || 0 : 0;
      const totalMeetings = meetingsResult.status === 'fulfilled' ? meetingsResult.value.count || 0 : 0;

      // Get password last changed from auth metadata (if available)
      let passwordLastChanged = null;
      try {
        const { data: { user: authUser } } = await supabase.auth.getUser();
        if (authUser?.user_metadata?.password_changed_at) {
          passwordLastChanged = new Date(authUser.user_metadata.password_changed_at);
        }
      } catch (error) {
        console.warn('Could not get password change date:', error);
      }

      setUserStats({
        daysInSystem,
        daysSinceLastLogin,
        totalContacts,
        totalActivities,
        totalMeetings,
        passwordLastChanged
      });

    } catch (error) {
      console.error('Error loading user stats:', error);
      // Keep default values on error
    } finally {
      setLoadingStats(false);
    }
  };
  const handleSaveProfile = async () => {
    if (!user) return;

    setProfileError(null);
    setProfileSuccess(null);

    if (!formData.name.trim()) {
      setProfileError('El nombre no puede estar vacío');
      return;
    }

    if (!formData.email.trim()) {
      setProfileError('El email no puede estar vacío');
      return;
    }

    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    if (!emailRegex.test(formData.email)) {
      setProfileError('El formato del email no es válido');
      return;
    }

    const emailChanged = formData.email.trim().toLowerCase() !== user.email.toLowerCase();

    if (emailChanged) {
      const confirmed = window.confirm(
        `Vas a cambiar el email de "${user.email}" a "${formData.email.trim().toLowerCase()}".\n\n` +
        `Importante: Necesitarás usar el nuevo email para iniciar sesión. ¿Continuar?`
      );
      if (!confirmed) return;
      setEmailChangePending(true);
    }

    setSaving(true);
    try {
      if (emailChanged) {
        const { error: authError } = await supabase.auth.updateUser({
          email: formData.email.trim().toLowerCase()
        });
        if (authError) throw authError;
      }

      await usersService.update(user.id, {
        name: formData.name.trim(),
        email: formData.email.trim().toLowerCase(),
        avatar: formData.avatar
      });

      setUser({ ...user, name: formData.name.trim(), email: formData.email.trim().toLowerCase(), avatar: formData.avatar });
      setIsEditing(false);

      if (emailChanged) {
        setProfileSuccess('Perfil actualizado. Se ha enviado un email de confirmación a la nueva dirección. Confirma el cambio desde ese email.');
        setEmailChangePending(false);
      } else {
        setProfileSuccess('Perfil actualizado correctamente');
      }

      await loadUserStats();
    } catch (error: any) {
      console.error('Error updating profile:', error);
      setProfileError('Error al actualizar el perfil: ' + (error.message || 'Error desconocido'));
      setEmailChangePending(false);
    } finally {
      setSaving(false);
    }
  };

  const handleAvatarUpload = async (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    if (!file) return;

    setImageError(null);
    setImageLoading(true);

    try {
      const validation = validateImageFile(file);

      if (!validation.valid) {
        setImageError(validation.error || 'Error validando imagen');
        setImageLoading(false);
        return;
      }

      const compressed = await compressImage(file, {
        maxWidth: 400,
        maxHeight: 400,
        quality: 0.8,
        maxSizeKB: 200
      });

      setFormData(prev => ({
        ...prev,
        avatar: compressed
      }));

      console.log('✅ Avatar cargado y comprimido correctamente');
    } catch (error) {
      console.error('Error procesando imagen:', error);
      setImageError('Error al procesar la imagen. Intenta con otra.');
    } finally {
      setImageLoading(false);
    }
  };

  const handleRemoveAvatar = () => {
    setFormData(prev => ({
      ...prev,
      avatar: ''
    }));
    setImageError(null);
  };

  const handlePasswordChange = async () => {
    setPasswordError(null);
    setPasswordSuccess(null);

    if (!passwordData.currentPassword) {
      setPasswordError('Debes introducir tu contraseña actual');
      return;
    }

    if (passwordData.newPassword !== passwordData.confirmPassword) {
      setPasswordError('Las contraseñas nuevas no coinciden');
      return;
    }

    if (passwordData.newPassword.length < 8) {
      setPasswordError('La contraseña debe tener al menos 8 caracteres');
      return;
    }

    const hasUpperCase = /[A-Z]/.test(passwordData.newPassword);
    const hasLowerCase = /[a-z]/.test(passwordData.newPassword);
    const hasNumber = /[0-9]/.test(passwordData.newPassword);

    if (!hasUpperCase || !hasLowerCase || !hasNumber) {
      setPasswordError('La contraseña debe contener mayúsculas, minúsculas y números');
      return;
    }

    if (passwordData.newPassword === passwordData.currentPassword) {
      setPasswordError('La nueva contraseña debe ser diferente a la actual');
      return;
    }

    setIsChangingPassword(true);
    try {
      // Verify current password without triggering a SIGNED_IN echo that would
      // re-run handleAuthenticatedUser and duplicate log entries.
      const ok = await authService.verifyCurrentPassword(user!.email, passwordData.currentPassword);
      if (!ok) {
        setPasswordError('La contraseña actual es incorrecta');
        return;
      }

      const { error } = await supabase.auth.updateUser({
        password: passwordData.newPassword,
        data: {
          password_changed_at: new Date().toISOString()
        }
      });

      if (error) throw error;

      setPasswordSuccess('Contraseña actualizada correctamente');
      setShowPasswordChange(false);
      setPasswordData({ currentPassword: '', newPassword: '', confirmPassword: '' });

      setUserStats(prev => ({
        ...prev,
        passwordLastChanged: new Date()
      }));
    } catch (error: any) {
      console.error('Error changing password:', error);
      setPasswordError('Error al cambiar la contraseña: ' + error.message);
    } finally {
      setIsChangingPassword(false);
    }
  };

  const loadUserAvailability = async () => {
    if (!user) return;

    try {
      setLoadingAvailability(true);
      const availability = await availabilityService.getUserAvailability(user.id);
      setAvailabilityData(availability);
      console.log('✅ Disponibilidad cargada:', availability);
    } catch (error) {
      console.error('Error loading availability:', error);
    } finally {
      setLoadingAvailability(false);
    }
  };

  const handleSaveAvailability = async (availabilityData: any) => {
    if (!user) return;

    try {
      console.log('💾 Guardando disponibilidad:', availabilityData);
      await availabilityService.saveUserAvailability(user.id, availabilityData);

      setShowAvailabilityModal(false);
      await loadUserAvailability();

      alert('✅ Disponibilidad configurada correctamente');
    } catch (error) {
      console.error('Error saving availability:', error);
      alert('❌ Error al guardar la disponibilidad');
    }
  };

  const handleCancelEdit = () => {
    if (!user) return;

    setFormData({
      name: user.name,
      email: user.email,
      avatar: user.avatar || ''
    });
    setIsEditing(false);
    setImageError(null);
  };

  const getRoleLabel = (userObj: UserType) => {
    const roleName = userObj.user_role?.name || userObj.role;
    const labels: Record<string, string> = {
      superadmin: 'Superadministrador',
      admin: 'Administrador',
      usuario_basico: 'Usuario',
      colaborador_programador: 'Programador',
      colaborador_comercial: 'Comercial',
      user: 'Usuario'
    };
    return labels[roleName] || userObj.user_role?.display_name || 'Usuario';
  };

  const getRoleColor = (userObj: UserType) => {
    const roleName = userObj.user_role?.name || userObj.role;
    const colors: Record<string, string> = {
      superadmin: 'bg-red-100 text-red-800',
      admin: 'bg-orange-100 text-orange-800',
      usuario_basico: 'bg-blue-100 text-blue-800',
      colaborador_programador: 'bg-green-100 text-green-800',
      colaborador_comercial: 'bg-teal-100 text-teal-800',
      user: 'bg-blue-100 text-blue-800'
    };
    return colors[roleName] || 'bg-blue-100 text-blue-800';
  };

  const formatPasswordDate = (date: Date | null) => {
    if (!date) return 'Nunca actualizada';
    
    const now = new Date();
    const diffDays = Math.floor((now.getTime() - date.getTime()) / (1000 * 60 * 60 * 24));
    
    if (diffDays === 0) return 'Hoy';
    if (diffDays === 1) return 'Ayer';
    if (diffDays < 30) return `Hace ${diffDays} días`;
    if (diffDays < 365) return `Hace ${Math.floor(diffDays / 30)} meses`;
    return `Hace ${Math.floor(diffDays / 365)} años`;
  };
  if (!user) {
    return (
      <div className="p-6 flex items-center justify-center">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600"></div>
        <span className="ml-3">Cargando perfil...</span>
      </div>
    );
  }

  return (
    <div className="p-6 max-w-4xl mx-auto">
      {/* Header */}
      <div className="mb-8">
        <h1 className="text-3xl font-bold text-gray-900 mb-2">Mi Perfil</h1>
        <p className="text-gray-600">Gestiona tu información personal y configuración de cuenta</p>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
        {/* Profile Card */}
        <div className="lg:col-span-1">
          <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-6">
            <div className="text-center">
              {/* Avatar */}
              <div className="relative inline-block mb-4">
                <div className="w-24 h-24 rounded-full overflow-hidden bg-gray-100 border-4 border-white shadow-lg">
                  {imageLoading ? (
                    <div className="w-full h-full bg-gray-100 flex items-center justify-center">
                      <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600"></div>
                    </div>
                  ) : formData.avatar ? (
                    <img
                      src={formData.avatar}
                      alt="Avatar"
                      className="w-full h-full object-cover"
                    />
                  ) : (
                    <div className="w-full h-full bg-blue-100 flex items-center justify-center">
                      <User className="w-8 h-8 text-blue-600" />
                    </div>
                  )}
                </div>
                {isEditing && (
                  <>
                    <div className="absolute bottom-0 right-0 flex space-x-1">
                      <input
                        type="file"
                        accept="image/*"
                        onChange={handleAvatarUpload}
                        className="hidden"
                        id="avatar-upload"
                        disabled={imageLoading}
                      />
                      <label
                        htmlFor="avatar-upload"
                        className="bg-blue-600 text-white p-2 rounded-full shadow-lg hover:bg-blue-700 cursor-pointer transition-colors disabled:opacity-50"
                      >
                        <Camera className="w-4 h-4" />
                      </label>
                      {formData.avatar && (
                        <button
                          type="button"
                          onClick={handleRemoveAvatar}
                          className="bg-red-600 text-white p-2 rounded-full shadow-lg hover:bg-red-700 transition-colors"
                          disabled={imageLoading}
                        >
                          <X className="w-4 h-4" />
                        </button>
                      )}
                    </div>
                  </>
                )}
              </div>

              {/* Image Error */}
              {imageError && (
                <div className="mb-4 p-3 bg-red-50 border border-red-200 rounded-lg flex items-start space-x-2">
                  <AlertCircle className="w-5 h-5 text-red-600 flex-shrink-0 mt-0.5" />
                  <p className="text-sm text-red-700">{imageError}</p>
                </div>
              )}

              {isEditing && (
                <p className="text-xs text-gray-500 mb-4">
                  Formatos permitidos: JPG, PNG, GIF, WebP. Tamaño máximo: 2MB
                </p>
              )}

              {/* User Info */}
              <h2 className="text-xl font-bold text-gray-900 mb-1">{user.name}</h2>
              <p className="text-gray-600 mb-3">{user.email}</p>
              
              <div className={`inline-flex items-center space-x-2 px-3 py-1 rounded-full ${getRoleColor(user)}`}>
                <Shield className="w-4 h-4" />
                <span className="text-sm font-medium">{getRoleLabel(user)}</span>
              </div>

              {/* Stats */}
              <div className="mt-6 grid grid-cols-1 gap-4 text-center">
                {loadingStats ? (
                  <div className="py-4">
                    <div className="animate-spin rounded-full h-6 w-6 border-b-2 border-blue-600 mx-auto mb-2"></div>
                    <p className="text-xs text-gray-500">Cargando estadísticas...</p>
                  </div>
                ) : (
                  <>
                    <div className="grid grid-cols-2 gap-4">
                      <div>
                        <p className="text-2xl font-bold text-gray-900">{userStats.daysInSystem}</p>
                        <p className="text-sm text-gray-600">Días en el sistema</p>
                      </div>
                      <div>
                        <p className="text-2xl font-bold text-gray-900">{userStats.daysSinceLastLogin}</p>
                        <p className="text-sm text-gray-600">Días desde último acceso</p>
                      </div>
                    </div>
                    
                    <div className="grid grid-cols-3 gap-2 mt-4 pt-4 border-t border-gray-200">
                      <div>
                        <p className="text-lg font-bold text-blue-600">{userStats.totalContacts}</p>
                        <p className="text-xs text-gray-600">Clientes</p>
                      </div>
                      <div>
                        <p className="text-lg font-bold text-green-600">{userStats.totalActivities}</p>
                        <p className="text-xs text-gray-600">Actividades</p>
                      </div>
                      <div>
                        <p className="text-lg font-bold text-purple-600">{userStats.totalMeetings}</p>
                        <p className="text-xs text-gray-600">Reuniones</p>
                      </div>
                    </div>
                  </>
                )}
              </div>
            </div>
          </div>
        </div>

        {/* Profile Details */}
        <div className="lg:col-span-2 space-y-6">
          {/* Personal Information */}
          <div className="bg-white rounded-xl shadow-sm border border-gray-200">
            <div className="p-6 border-b border-gray-200">
              <div className="flex items-center justify-between">
                <h3 className="text-lg font-semibold text-gray-900">Información Personal</h3>
                <button
                  onClick={() => {
                    if (isEditing) {
                      handleCancelEdit();
                    } else {
                      setProfileError(null);
                      setProfileSuccess(null);
                      setIsEditing(true);
                    }
                  }}
                  className="flex items-center space-x-2 px-3 py-2 text-sm border border-gray-300 rounded-lg hover:bg-gray-50 transition-colors"
                >
                  {isEditing ? (
                    <>
                      <X className="w-4 h-4" />
                      <span>Cancelar</span>
                    </>
                  ) : (
                    <>
                      <Settings className="w-4 h-4" />
                      <span>Editar</span>
                    </>
                  )}
                </button>
              </div>
            </div>

            <div className="p-6 space-y-4">
              {profileError && (
                <div className="flex items-start space-x-2 p-3 bg-red-50 border border-red-200 rounded-lg">
                  <AlertCircle className="w-4 h-4 text-red-600 flex-shrink-0 mt-0.5" />
                  <p className="text-sm text-red-700">{profileError}</p>
                </div>
              )}
              {profileSuccess && (
                <div className="flex items-start space-x-2 p-3 bg-green-50 border border-green-200 rounded-lg">
                  <AlertCircle className="w-4 h-4 text-green-600 flex-shrink-0 mt-0.5" />
                  <p className="text-sm text-green-700">{profileSuccess}</p>
                </div>
              )}
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Nombre completo
                  </label>
                  {isEditing ? (
                    <input
                      type="text"
                      value={formData.name}
                      onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                    />
                  ) : (
                    <p className="text-gray-900 py-2">{user.name}</p>
                  )}
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Email
                  </label>
                  {isEditing ? (
                    <input
                      type="email"
                      value={formData.email}
                      onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                    />
                  ) : (
                    <p className="text-gray-900 py-2">{user.email}</p>
                  )}
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Rol del sistema
                  </label>
                  <div className="py-2">
                    <span className={`inline-flex items-center space-x-2 px-3 py-1 rounded-full ${getRoleColor(user)}`}>
                      <Shield className="w-4 h-4" />
                      <span className="text-sm font-medium">{getRoleLabel(user)}</span>
                    </span>
                  </div>
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Estado de la cuenta
                  </label>
                  <div className="py-2">
                    <span className={`inline-flex items-center space-x-2 px-3 py-1 rounded-full ${
                      user.isActive ? 'bg-green-100 text-green-800' : 'bg-red-100 text-red-800'
                    }`}>
                      {user.isActive ? <Eye className="w-4 h-4" /> : <EyeOff className="w-4 h-4" />}
                      <span className="text-sm font-medium">{user.isActive ? 'Activa' : 'Inactiva'}</span>
                    </span>
                  </div>
                </div>
              </div>

              {isEditing && (
                <div className="flex justify-end space-x-3 pt-4 border-t border-gray-200">
                  <button
                    onClick={handleCancelEdit}
                    className="px-4 py-2 border border-gray-300 rounded-lg text-gray-700 hover:bg-gray-50 transition-colors"
                    disabled={isSaving}
                  >
                    Cancelar
                  </button>
                  <button
                    onClick={handleSaveProfile}
                    disabled={isSaving || imageLoading}
                    className="flex items-center space-x-2 px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 disabled:opacity-50 transition-colors"
                  >
                    {isSaving ? (
                      <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white"></div>
                    ) : (
                      <Save className="w-4 h-4" />
                    )}
                    <span>{isSaving ? 'Guardando...' : 'Guardar Cambios'}</span>
                  </button>
                </div>
              )}
            </div>
          </div>

          {/* Security Settings */}
          <div className="bg-white rounded-xl shadow-sm border border-gray-200">
            <div className="p-6 border-b border-gray-200">
              <h3 className="text-lg font-semibold text-gray-900">Seguridad</h3>
            </div>

            <div className="p-6 space-y-4">
              <div className="flex items-center justify-between">
                <div>
                  <h4 className="text-sm font-medium text-gray-900">Contraseña</h4>
                  <p className="text-sm text-gray-600">
                    Última actualización: {formatPasswordDate(userStats.passwordLastChanged)}
                  </p>
                </div>
                <button
                  onClick={() => setShowPasswordChange(!showPasswordChange)}
                  className="flex items-center space-x-2 px-3 py-2 border border-gray-300 rounded-lg hover:bg-gray-50 transition-colors text-sm"
                >
                  <Lock className="w-4 h-4" />
                  <span>Cambiar Contraseña</span>
                </button>
              </div>

              <MfaSettings />

              {passwordSuccess && !showPasswordChange && (
                <div className="flex items-start space-x-2 p-3 bg-green-50 border border-green-200 rounded-lg">
                  <AlertCircle className="w-4 h-4 text-green-600 flex-shrink-0 mt-0.5" />
                  <p className="text-sm text-green-700">{passwordSuccess}</p>
                </div>
              )}

              {showPasswordChange && (
                <div className="bg-gray-50 rounded-lg p-4 space-y-3">
                  {passwordError && (
                    <div className="flex items-start space-x-2 p-3 bg-red-50 border border-red-200 rounded-lg">
                      <AlertCircle className="w-4 h-4 text-red-600 flex-shrink-0 mt-0.5" />
                      <p className="text-sm text-red-700">{passwordError}</p>
                    </div>
                  )}
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      Contraseña actual *
                    </label>
                    <input
                      type="password"
                      value={passwordData.currentPassword}
                      onChange={(e) => { setPasswordData({ ...passwordData, currentPassword: e.target.value }); setPasswordError(null); }}
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                      placeholder="Tu contraseña actual"
                      autoComplete="current-password"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      Nueva contraseña *
                    </label>
                    <input
                      type="password"
                      value={passwordData.newPassword}
                      onChange={(e) => { setPasswordData({ ...passwordData, newPassword: e.target.value }); setPasswordError(null); }}
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                      placeholder="Mínimo 8 caracteres"
                      autoComplete="new-password"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      Confirmar nueva contraseña *
                    </label>
                    <input
                      type="password"
                      value={passwordData.confirmPassword}
                      onChange={(e) => { setPasswordData({ ...passwordData, confirmPassword: e.target.value }); setPasswordError(null); }}
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                      placeholder="Repite la nueva contraseña"
                      autoComplete="new-password"
                    />
                  </div>
                  <div className="bg-blue-50 border border-blue-200 rounded p-3">
                    <p className="text-xs text-blue-700">
                      <strong>Requisitos:</strong> Mínimo 8 caracteres, debe incluir mayúsculas, minúsculas y números.
                    </p>
                  </div>
                  <div className="flex justify-end space-x-2">
                    <button
                      onClick={() => { setShowPasswordChange(false); setPasswordData({ currentPassword: '', newPassword: '', confirmPassword: '' }); setPasswordError(null); }}
                      disabled={isChangingPassword}
                      className="px-3 py-2 border border-gray-300 rounded-lg text-gray-700 hover:bg-gray-50 transition-colors text-sm disabled:opacity-50"
                    >
                      Cancelar
                    </button>
                    <button
                      onClick={handlePasswordChange}
                      disabled={isChangingPassword || !passwordData.currentPassword || !passwordData.newPassword || !passwordData.confirmPassword}
                      className="px-3 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 disabled:opacity-50 transition-colors text-sm flex items-center space-x-2"
                    >
                      {isChangingPassword && <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white"></div>}
                      <span>{isChangingPassword ? 'Verificando...' : 'Actualizar Contraseña'}</span>
                    </button>
                  </div>
                </div>
              )}
            </div>
          </div>

          {/* Account Information */}
          <div className="bg-white rounded-xl shadow-sm border border-gray-200">
            <div className="p-6 border-b border-gray-200">
              <h3 className="text-lg font-semibold text-gray-900">Información de la Cuenta</h3>
            </div>

            <div className="p-6">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                <div className="flex items-center space-x-3">
                  <div className="p-2 bg-blue-100 rounded-lg">
                    <Calendar className="w-5 h-5 text-blue-600" />
                  </div>
                  <div>
                    <p className="text-sm font-medium text-gray-900">Miembro desde</p>
                    <p className="text-sm text-gray-600">
                      {(() => {
                        try {
                          if (!user.createdAt) return 'Fecha no disponible';
                          if (!(user.createdAt instanceof Date)) return 'Fecha inválida';
                          return user.createdAt.toLocaleDateString('es-ES', {
                            year: 'numeric',
                            month: 'long',
                            day: 'numeric'
                          });
                        } catch (error) {
                          console.error('Error formatting createdAt:', error, user.createdAt);
                          return 'Error en fecha';
                        }
                      })()}
                    </p>
                  </div>
                </div>

                <div className="flex items-center space-x-3">
                  <div className="p-2 bg-green-100 rounded-lg">
                    <Activity className="w-5 h-5 text-green-600" />
                  </div>
                  <div>
                    <p className="text-sm font-medium text-gray-900">Último acceso</p>
                    <p className="text-sm text-gray-600">
                      {(() => {
                        try {
                          if (!user.lastLogin) return 'Primer acceso';
                          if (!(user.lastLogin instanceof Date)) return 'Fecha inválida';
                          return user.lastLogin.toLocaleDateString('es-ES', {
                            year: 'numeric',
                            month: 'long',
                            day: 'numeric',
                            hour: '2-digit',
                            minute: '2-digit'
                          });
                        } catch (error) {
                          console.error('Error formatting lastLogin:', error, user.lastLogin);
                          return 'Error en fecha';
                        }
                      })()}
                    </p>
                  </div>
                </div>

                <div className="flex items-center space-x-3">
                  <div className="p-2 bg-purple-100 rounded-lg">
                    <Shield className="w-5 h-5 text-purple-600" />
                  </div>
                  <div>
                    <p className="text-sm font-medium text-gray-900">ID de usuario</p>
                    <p className="text-xs text-gray-500 font-mono">{user.id}</p>
                  </div>
                </div>

                <div className="flex items-center space-x-3">
                  <div className="p-2 bg-orange-100 rounded-lg">
                    <User className="w-5 h-5 text-orange-600" />
                  </div>
                  <div>
                    <p className="text-sm font-medium text-gray-900">Creado por</p>
                    <p className="text-sm text-gray-600">
                      {user.createdBy ? 'Administrador del sistema' : 'Registro propio'}
                    </p>
                  </div>
                </div>
              </div>
            </div>
          </div>

          {/* Availability Configuration */}
          <div className="bg-white rounded-xl shadow-sm border border-gray-200">
            <div className="p-6 border-b border-gray-200">
              <div className="flex items-center justify-between">
                <h3 className="text-lg font-semibold text-gray-900">Disponibilidad para Reuniones</h3>
                <button
                  onClick={() => setShowAvailabilityModal(true)}
                  className="flex items-center space-x-2 px-3 py-2 border border-gray-300 rounded-lg hover:bg-gray-50 transition-colors text-sm"
                >
                  <Clock className="w-4 h-4" />
                  <span>Configurar Horarios</span>
                </button>
              </div>
            </div>

            <div className="p-6">
              {loadingAvailability ? (
                <div className="text-center py-8">
                  <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600 mx-auto mb-2"></div>
                  <p className="text-sm text-gray-500">Cargando disponibilidad...</p>
                </div>
              ) : availabilityData ? (
                (() => {
                const availability = availabilityData;
                if (availability) {
                  // Handle both old and new format
                  let workingDaysLabels = '';
                  if (availability.dailySchedules) {
                    // New format
                    const days = ['Dom', 'Lun', 'Mar', 'Mié', 'Jue', 'Vie', 'Sáb'];
                    const workingDays = Object.entries(availability.dailySchedules)
                      .filter(([day, schedule]) => schedule.isWorking)
                      .map(([day]) => days[parseInt(day)]);
                    workingDaysLabels = workingDays.join(', ');
                  } else if (availability.workingDays) {
                    // Old format
                    const days = ['Dom', 'Lun', 'Mar', 'Mié', 'Jue', 'Vie', 'Sáb'];
                    workingDaysLabels = availability.workingDays.map((d: number) => days[d]).join(', ');
                  }
                  
                  return (
                    <div className="space-y-4">
                      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                        <div>
                          <p className="text-sm font-medium text-gray-700">Días laborables</p>
                          <p className="text-gray-900">{workingDaysLabels}</p>
                        </div>
                        <div>
                          <p className="text-sm font-medium text-gray-700">Horario</p>
                          <p className="text-gray-900">
                            {availability.dailySchedules ? 
                              Object.entries(availability.dailySchedules)
                                .filter(([day, schedule]) => schedule.isWorking)
                                .map(([day, schedule]) => `${schedule.start}-${schedule.end}`)
                                .join(', ') || 'Sin horarios configurados'
                              : availability.workingHours ? 
                                `${availability.workingHours.start} - ${availability.workingHours.end}`
                                : 'Sin horarios configurados'
                            }
                          </p>
                        </div>
                        <div>
                          <p className="text-sm font-medium text-gray-700">Zona horaria</p>
                          <p className="text-gray-900">{availability.timezone}</p>
                        </div>
                        <div>
                          <p className="text-sm font-medium text-gray-700">Visibilidad</p>
                          <div className="flex items-center space-x-2">
                            <span className={`inline-flex px-2 py-1 text-xs font-medium rounded-full ${
                              availability.isActive ? 'bg-green-100 text-green-800' : 'bg-red-100 text-red-800'
                            }`}>
                              {availability.isActive ? 'Activo' : 'Inactivo'}
                            </span>
                            <span className={`inline-flex px-2 py-1 text-xs font-medium rounded-full ${
                              availability.isVisibleInPublicAgenda ? 'bg-blue-100 text-blue-800' : 'bg-gray-100 text-gray-800'
                            }`}>
                              {availability.isVisibleInPublicAgenda ? 'Público' : 'Privado'}
                            </span>
                          </div>
                        </div>
                      </div>
                      
                      {availability.breaks && availability.breaks.length > 0 && (
                        <div>
                          <p className="text-sm font-medium text-gray-700 mb-2">Descansos configurados</p>
                          <div className="space-y-1">
                            {availability.breaks.map((brk: any, index: number) => (
                              <p key={index} className="text-sm text-gray-600">
                                • {brk.label}: {brk.start} - {brk.end}
                              </p>
                            ))}
                          </div>
                        </div>
                      )}
                      
                      {availability.blockedDates && availability.blockedDates.length > 0 && (
                        <div>
                          <p className="text-sm font-medium text-gray-700 mb-2">Fechas bloqueadas</p>
                          <p className="text-sm text-gray-600">{availability.blockedDates.length} fechas bloqueadas</p>
                        </div>
                      )}
                    </div>
                  );
                }
                return null;
                })()
              ) : (
                    <div className="text-center py-8">
                      <Clock className="w-12 h-12 text-gray-300 mx-auto mb-4" />
                      <h4 className="text-lg font-medium text-gray-900 mb-2">Sin disponibilidad configurada</h4>
                      <p className="text-gray-500 mb-4">
                        Configura tu horario para aparecer en la agenda pública
                      </p>
                      <button
                        onClick={() => setShowAvailabilityModal(true)}
                        className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors"
                      >
                        Configurar Disponibilidad
                      </button>
                    </div>
              )}
            </div>
          </div>

          {/* Activity Summary */}
          {!loadingStats && (userStats.totalContacts > 0 || userStats.totalActivities > 0 || userStats.totalMeetings > 0) && (
            <div className="bg-white rounded-xl shadow-sm border border-gray-200">
              <div className="p-6 border-b border-gray-200">
                <h3 className="text-lg font-semibold text-gray-900">Resumen de Actividad</h3>
              </div>

              <div className="p-6">
                <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
                  <div className="text-center p-4 bg-blue-50 rounded-lg">
                    <div className="text-2xl font-bold text-blue-600 mb-1">{userStats.totalContacts}</div>
                    <p className="text-sm text-blue-700">Clientes gestionados</p>
                  </div>
                  
                  <div className="text-center p-4 bg-green-50 rounded-lg">
                    <div className="text-2xl font-bold text-green-600 mb-1">{userStats.totalActivities}</div>
                    <p className="text-sm text-green-700">Actividades registradas</p>
                  </div>
                  
                  <div className="text-center p-4 bg-purple-50 rounded-lg">
                    <div className="text-2xl font-bold text-purple-600 mb-1">{userStats.totalMeetings}</div>
                    <p className="text-sm text-purple-700">Reuniones asignadas</p>
                  </div>
                </div>
              </div>
            </div>
          )}
          {/* Permissions */}
          {user.role === 'superadmin' && (
            <div className="bg-white rounded-xl shadow-sm border border-gray-200">
              <div className="p-6 border-b border-gray-200">
                <h3 className="text-lg font-semibold text-gray-900">Permisos de Administrador</h3>
              </div>

              <div className="p-6">
                <div className="space-y-3">
                  {[
                    'Gestionar todos los contactos y empresas',
                    'Ver todas las actividades y reuniones',
                    'Administrar usuarios del sistema',
                    'Acceder a configuración avanzada',
                    'Ver reportes y estadísticas completas',
                    'Gestionar formularios y integraciones'
                  ].map((permission, index) => (
                    <div key={index} className="flex items-center space-x-2">
                      <div className="w-2 h-2 bg-green-500 rounded-full"></div>
                      <span className="text-sm text-gray-700">{permission}</span>
                    </div>
                  ))}
                </div>
              </div>
            </div>
          )}
        </div>
      </div>

      {/* Availability Modal */}
      <UserAvailabilityModal
        isOpen={showAvailabilityModal}
        onClose={() => setShowAvailabilityModal(false)}
        onSave={handleSaveAvailability}
        availability={availabilityData}
        userName={user.name}
      />
    </div>
  );
};

export default UserProfile;